<template>
  <div>
    <div class="registration-feedback feedback">
      <transition name="fade" @enter="enterError">
        <FeedbackError v-show="hasErrors" message="登録できませんでした" />
      </transition>
    </div>
    <div class="container registration-container">
      <section class="registration-section">
        <div class="registration-section__head">
          <PageHeadline title="プロフィール登録" />
        </div>
        <div class="registration-section__body">
          <div class="registration-form">
            <form :action="postUrl" method="POST" accept-charset="UTF-8">
              <input name="utf8" type="hidden" value="✓" />
              <input
                type="hidden"
                name="authenticity_token"
                :value="authenticityToken"
              />
              <div class="registration-form__profile">
                <div class="registration-error-message">
                  <div v-if="errorMessages.length >= 1" class="box-list--alert">
                    <div
                      v-for="(errorMessage, index) in errorMessages"
                      :key="index"
                      class="error_message"
                    >
                      {{ errorMessage }}
                    </div>
                  </div>
                </div>
                <div class="caption registration-form__caption">
                  <span class="require"> 必須 </span>
                </div>
                <div
                  class="registration-form__nickname registration-form__group"
                >
                  <div class="registration-form__label caption-bold">
                    <span class="require">
                      ニックネーム（後からでも編集可能です）
                    </span>
                  </div>
                  <div class="registration-form__attention">
                    ギフトを贈る際に贈り主の名前として使用します
                  </div>
                  <input
                    id="user_registration_form_nickname"
                    v-model="nickname"
                    name="user_registration_form[nickname]"
                    :class="{
                      field_with_errors: hasNicknameError,
                      'input-form--inputted': isNicknameInputted,
                      'input-form': !isNicknameInputted,
                      'input-form--inputting': isNicknameFocused
                    }"
                    placeholder="例：ギフティ太郎"
                    maxlength="20"
                    @focus="onFocusNickname"
                    @blur="onBlurNickname"
                  />
                  <div class="registration-form__attention">※20文字以内</div>
                </div>
                <div class="registration-form__email registration-form__group">
                  <div class="registration-form__label caption-bold">
                    <span class="require"> メールアドレス </span>
                  </div>
                  <input
                    id="user_registration_form_email"
                    v-model="email"
                    name="user_registration_form[email]"
                    :class="{
                      field_with_errors: hasEmailError,
                      'input-form--inputted': isEmailInputted,
                      'input-form': !isEmailInputted,
                      'input-form--inputting': isEmailFocused,
                      'email-input--readonly': emailReadonly
                    }"
                    :readonly="emailReadonly || null"
                    @focus="onFocusEmail"
                    @blur="onBlurEmail"
                  />
                </div>
                <div class="registration-form__gender registration-form__group">
                  <div class="registration-form__label caption-bold">
                    <span class="require"> 性別 </span>
                  </div>
                  <div class="registration-form__radio-buttons">
                    <div class="radio_btn">
                      <input
                        id="user_registration_form_gender_man"
                        v-model="gender"
                        name="user_registration_form[gender]"
                        type="radio"
                        value="0"
                        :class="{ field_with_errors: hasGenderError }"
                      />
                      <label
                        class="radio-label"
                        for="user_registration_form_gender_man"
                      >
                        男性
                      </label>
                    </div>
                    <div class="radio_btn">
                      <input
                        id="user_registration_form_gender_woman"
                        v-model="gender"
                        name="user_registration_form[gender]"
                        type="radio"
                        value="1"
                        :class="{ field_with_errors: hasGenderError }"
                      />
                      <label
                        class="radio-label"
                        for="user_registration_form_gender_woman"
                      >
                        女性
                      </label>
                    </div>
                    <div class="radio_btn">
                      <input
                        id="user_registration_form_gender_unknown"
                        v-model="gender"
                        name="user_registration_form[gender]"
                        type="radio"
                        value="2"
                        :class="{ field_with_errors: hasGenderError }"
                      />
                      <label
                        class="radio-label"
                        for="user_registration_form_gender_unknown"
                      >
                        その他
                      </label>
                    </div>
                  </div>
                </div>
                <div
                  class="registration-form__birthday registration-form__group"
                >
                  <div class="registration-form__label caption-bold">
                    <span> 誕生日 </span>
                  </div>
                  <div class="registration-form__attention">
                    プロフィールに使用できます
                  </div>
                  <div class="registration-form__selects">
                    <div class="registration-form__select-pair">
                      <select
                        id="user_registration_form_birth_year"
                        v-model="birthYear"
                        name="user_registration_form[birth_year]"
                        class="registration-form__select registration-form__select--four-digit"
                        :class="{
                          'select-form--selected': isBirthYearSelected,
                          'select-form--unselected': !isBirthYearSelected,
                          field_with_errors: hasBirthYearError
                        }"
                      >
                        <option
                          v-for="index in 90"
                          :key="1900 + index"
                          :value="`${1900 + index}`"
                        >
                          {{ 1900 + index }}
                        </option>
                        <option value="" />
                        <option
                          v-for="index in currentYear - 1990"
                          :key="1990 + index"
                          :value="`${1990 + index}`"
                        >
                          {{ 1990 + index }}
                        </option>
                      </select>
                      <span class="registration-form__select-unit"> 年 </span>
                    </div>
                    <div class="registration-form__select-pair">
                      <select
                        id="user_registration_form_birth_month"
                        v-model="birthMonth"
                        name="user_registration_form[birth_month]"
                        class="registration-form__select registration-form__select--two-digit"
                        :class="{
                          'select-form--selected': isBirthMonthSelected,
                          'select-form--unselected': !isBirthMonthSelected,
                          field_with_errors: hasBirthMonthError
                        }"
                      >
                        <option value="" />
                        <option
                          v-for="index in 12"
                          :key="index"
                          :value="index.toString()"
                        >
                          {{ index }}
                        </option>
                      </select>
                      <span class="registration-form__select-unit"> 月 </span>
                    </div>
                    <div class="registration-form__select-pair">
                      <select
                        id="user_registration_form_birth_day"
                        v-model="birthDay"
                        name="user_registration_form[birth_day]"
                        class="registration-form__select registration-form__select--two-digit"
                        :class="{
                          'select-form--selected': isBirthDaySelected,
                          'select-form--unselected': !isBirthDaySelected,
                          field_with_errors: hasBirthDayError
                        }"
                      >
                        <option value="" />
                        <option
                          v-for="index in 31"
                          :key="index"
                          :value="index.toString()"
                        >
                          {{ index }}
                        </option>
                      </select>
                      <span class="registration-form__select-unit"> 日 </span>
                    </div>
                  </div>
                  <div class="registration-form__box-list box-list--attention">
                    <p>※公開するには別途プロフィール設定画面で設定が必要です</p>
                    <p>※登録した誕生日は後から変更できません</p>
                  </div>
                </div>
              </div>
              <div class="registration-form__notification">
                <div class="registration-form__group">
                  <div class="registration-form__notification-title">
                    メール
                  </div>
                  <div class="registration-form__attention">
                    後からでも変更可能です
                  </div>
                  <div class="registration-form__checkbox">
                    <label
                      for="user_registration_form_receive_information_mail"
                    >
                      <div
                        class="registration-form__information-mail"
                        :class="
                          isInformationMailChecked
                            ? 'checkbox--checked'
                            : 'checkbox'
                        "
                      >
                        <input
                          id="user_registration_form_receive_information_mail"
                          v-model="isInformationMailChecked"
                          type="checkbox"
                          name="user_registration_form[receive_information_mail]"
                        />
                        gifteeからのお知らせ
                      </div>
                    </label>
                    <div class="registration-form__attention">
                      新商品情報・キャンペーン情報などをお知らせします
                    </div>
                  </div>
                  <div class="registration-form__checkbox">
                    <label for="user_registration_form_receive_benefit_mail">
                      <div
                        class="registration-form__receive-benefit-mail"
                        :class="
                          isBenefitMailChecked
                            ? 'checkbox--checked'
                            : 'checkbox'
                        "
                      >
                        <input
                          id="user_registration_form_receive_benefit_mail"
                          v-model="isBenefitMailChecked"
                          type="checkbox"
                          name="user_registration_form[receive_benefit_mail]"
                        />
                        特典ギフトとギフトコード付与のお知らせ
                      </div>
                    </label>
                    <div class="registration-form__attention">
                      gifteeから特典ギフトやギフトコードが付与されたときにお知らせします
                    </div>
                  </div>
                  <div class="registration-form__checkbox">
                    <label
                      for="user_registration_form_receive_usable_gift_mail"
                    >
                      <div
                        class="registration-form__usable-gift"
                        :class="
                          isUsableGiftChecked ? 'checkbox--checked' : 'checkbox'
                        "
                      >
                        <input
                          id="user_registration_form_receive_usable_gift_mail"
                          v-model="isUsableGiftChecked"
                          type="checkbox"
                          name="user_registration_form[receive_usable_gift_mail]"
                        />
                        ギフトとギフトコードの利用リマインド
                      </div>
                    </label>
                    <div class="registration-form__attention">
                      保存しているギフトがある場合は毎月20日に、未使用のギフトコードがある場合は期日の3日前にお知らせします
                    </div>
                  </div>
                  <div class="registration-form__checkbox">
                    <label
                      for="user_registration_form_receive_follow_user_birthday_mail"
                    >
                      <div
                        class="registration-form__follow-user-birthday"
                        :class="
                          isFollowUserBirthdayChecked
                            ? 'checkbox--checked'
                            : 'checkbox'
                        "
                      >
                        <input
                          id="user_registration_form_receive_follow_user_birthday_mail"
                          v-model="isFollowUserBirthdayChecked"
                          type="checkbox"
                          name="user_registration_form[receive_follow_user_birthday_mail]"
                        />
                        誕生日のお知らせ
                      </div>
                    </label>
                    <div class="registration-form__attention">
                      プロフィール画面からフォローした相手が誕生日を公開している場合、当日にお知らせします
                    </div>
                  </div>
                  <div class="registration-form__checkbox">
                    <label
                      for="user_registration_form_receive_wish_list_limit_mail"
                    >
                      <div
                        class="registration-form__receive-wish-list-limit-mail"
                        :class="
                          isWishListLimitMailChecked
                            ? 'checkbox--checked'
                            : 'checkbox'
                        "
                      >
                        <input
                          id="user_registration_form_receive_wish_list_limit_mail"
                          v-model="isWishListLimitMailChecked"
                          type="checkbox"
                          name="user_registration_form[receive_wish_list_limit_mail]"
                        />
                        受け取りたい個数設定に関するお知らせ
                      </div>
                    </label>
                    <div class="registration-form__attention">
                      ほしいものリストに登録したギフトに受け取りたい個数を設定している場合、個数上限に到達した際にお知らせします
                    </div>
                  </div>
                </div>
              </div>
              <div class="registration-form__submit">
                <div class="registration-form__submit-content">
                  <button
                    type="submit"
                    class="btn btn--primary"
                    :disabled="!isSubmittable || null"
                    data-disable-with="登録する"
                  >
                    登録する
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>
<script>
import FeedbackError from '../../atoms/feedbacks/feedback-error.vue'
import PageHeadline from '../../../components/molecules/headlines/PageHeadline.vue'
export default {
  components: { FeedbackError, PageHeadline },
  props: {
    authenticityToken: {
      type: String,
      required: true
    },
    postUrl: {
      type: String,
      required: true
    },
    errorMessages: {
      type: Array,
      required: false,
      default: () => []
    },
    initialNickname: {
      type: String,
      required: false,
      default: ''
    },
    initialEmail: {
      type: String,
      required: false,
      default: ''
    },
    emailReadonly: {
      type: Boolean,
      required: true
    },
    initialGender: {
      type: String,
      required: false,
      default: ''
    },
    initialBirthYear: {
      type: String,
      required: false,
      default: ''
    },
    initialBirthMonth: {
      type: String,
      required: false,
      default: ''
    },
    initialBirthDay: {
      type: String,
      required: false,
      default: ''
    },
    initialInformationMail: {
      type: String,
      required: false,
      default: ''
    },
    initialUsableGift: {
      type: String,
      required: false,
      default: ''
    },
    initialBirthdayMail: {
      type: String,
      required: false,
      default: ''
    },
    initialBenefitMail: {
      type: String,
      required: false,
      default: ''
    },
    initialWishListLimitMail: {
      type: String,
      required: false,
      default: ''
    },
    hasNicknameError: {
      type: Boolean,
      required: false,
      default: false
    },
    hasEmailError: {
      type: Boolean,
      required: false,
      default: false
    },
    hasGenderError: {
      type: Boolean,
      required: false,
      default: false
    },
    hasBirthYearError: {
      type: Boolean,
      required: false,
      default: false
    },
    hasBirthMonthError: {
      type: Boolean,
      required: false,
      default: false
    },
    hasBirthDayError: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      nickname: '',
      isNicknameFocused: false,
      email: '',
      isEmailFocused: false,
      gender: '',
      birthYear: '',
      birthMonth: '',
      birthDay: '',
      isInformationMailChecked: true,
      isUsableGiftChecked: true,
      isFollowUserBirthdayChecked: true,
      isBenefitMailChecked: true,
      isWishListLimitMailChecked: true,
      hasErrors: false
    }
  },
  computed: {
    currentYear() {
      const date = new Date()
      return date.getFullYear()
    },
    isNicknameInputted() {
      return this.nickname.length !== 0
    },
    isEmailInputted() {
      return this.email.length !== 0
    },
    isGenderSelected() {
      return ['0', '1', '2'].includes(this.gender)
    },
    isBirthDayEmpty() {
      return (
        !this.isBirthYearSelected &&
        !this.isBirthMonthSelected &&
        !this.isBirthDaySelected
      )
    },
    isBirthDateCompleted() {
      return (
        this.isBirthYearSelected &&
        this.isBirthMonthSelected &&
        this.isBirthDaySelected
      )
    },
    isBirthYearSelected() {
      return this.birthYear !== ''
    },
    isBirthMonthSelected() {
      return this.birthMonth !== ''
    },
    isBirthDaySelected() {
      return this.birthDay !== ''
    },
    isSubmittable() {
      return (
        this.isEmailInputted &&
        this.isNicknameInputted &&
        this.isGenderSelected &&
        (this.isBirthDayEmpty || this.isBirthDateCompleted)
      )
    }
  },
  created() {
    if (this.errorMessages.length >= 1) {
      this.hasErrors = true
      this.enterError()
    }

    if (this.initialNickname) {
      this.nickname = this.initialNickname
    }

    if (this.initialEmail) {
      this.email = this.initialEmail
    }

    if (this.initialGender) {
      this.gender = this.initialGender
    }

    if (this.initialBirthYear) {
      this.birthYear = this.initialBirthYear
    }

    if (this.initialBirthMonth) {
      this.birthMonth = this.initialBirthMonth
    }

    if (this.initialBirthDay) {
      this.birthDay = this.initialBirthDay
    }

    if (this.initialInformationMail === 'false') {
      this.isInformationMailChecked = false
    }

    if (this.initialUsableGift === 'false') {
      this.isUsableGiftChecked = false
    }

    if (this.initialBirthdayMail === 'false') {
      this.isFollowUserBirthdayChecked = false
    }

    if (this.initialBenefitMail === 'false') {
      this.isBenefitMailChecked = false
    }

    if (this.initialWishListLimitMail === 'false') {
      this.isWishListLimitMailChecked = false
    }
  },
  methods: {
    onFocusNickname() {
      this.isNicknameFocused = true
    },
    onBlurNickname() {
      this.isNicknameFocused = false
    },
    onFocusEmail() {
      this.isEmailFocused = true
    },
    onBlurEmail() {
      this.isEmailFocused = false
    },
    enterError() {
      setTimeout(() => {
        this.hasErrors = false
      }, 3000)
    }
  }
}
</script>
<style lang="scss" scoped>
.radio_btn input.field_with_errors[type='radio'] + .radio-label:before {
  border-color: $danger;
}
</style>
